import { watch } from "vue";

import { useGlobalStore } from "@stores/global";
import { useGlobeStore } from "@stores/globe";
import { useTriviaStore } from "@stores/trivia";
import { useDataStore } from "@stores/data";

import CameraRig from "../utils/three/cameraRig";

export function useCamera() {
    const globalStore = useGlobalStore();
    const globeStore = useGlobeStore();
    const triviaStore = useTriviaStore();
    const dataStore = useDataStore();

    const cameraRig = new CameraRig();

    function _syncAutoRotate() {
        if (globalStore.mode === "landing") return;

        const page = globalStore.activePage;

        if (page === "trivia") {
            cameraRig.setAutoRotate(false);
        } else if (page === "explore") {
            cameraRig.setAutoRotate(
                globeStore.autoRotateEnabled && !globeStore.selectedCountry,
            );
        } else if (page === "settings") {
            cameraRig.setAutoRotate(globeStore.autoRotateEnabled);
        } else {
            cameraRig.setAutoRotate(true);
        }
    }

    watch(
        () => globalStore.countdown,
        (isCountdown) => {
            cameraRig.setLandingVariant(isCountdown, window.innerWidth <= 1024);
        },
        { immediate: true },
    );

    watch(
        () => globalStore.mode,
        (mode) => {
            if (mode === "playing") {
                const page = globalStore.activePage;

                cameraRig.setPlayingPreset(
                    page === "trivia"
                        ? "trivia"
                        : page === "achievements"
                          ? "achievements"
                          : page === "vote"
                            ? "vote"
                            : "default",
                );
                cameraRig.transitionToPlaying();
            } else {
                cameraRig.transitionToLanding();
            }

            _syncAutoRotate();
        },
    );

    watch(
        () => globalStore.activePage,
        (page, prevPage) => {
            if (globalStore.mode !== "playing") return;

            cameraRig.cancelFocus();

            if (page === "trivia") {
                cameraRig.setPlayingPreset("trivia");
            } else if (page === "achievements") {
                cameraRig.setPlayingPreset("achievements");
            } else if (page === "vote") {
                cameraRig.setPlayingPreset("vote");
            } else if (
                page === "explore" ||
                prevPage === "trivia" ||
                prevPage === "achievements" ||
                prevPage === "vote" ||
                (prevPage === "explore" && globeStore.selectedCountry)
            ) {
                cameraRig.setPlayingPreset("default");
            } else {
                cameraRig.enableControlsWithoutTransition();
            }

            _syncAutoRotate();
        },
    );

    watch(
        [() => globeStore.autoRotateEnabled, () => globeStore.selectedCountry],
        _syncAutoRotate,
    );

    watch(
        () => triviaStore.gameState,
        (state) => {
            if (globalStore.activePage !== "trivia") return;
            if (state === "playing") {
                cameraRig.setPlayingPreset("triviaPlaying");
                cameraRig.setAutoRotate(true);
            } else {
                cameraRig.setAutoRotate(false);
                if (state === "intro") {
                    cameraRig.setPlayingPreset("trivia");
                }
            }
        },
    );

    watch(
        () => globeStore.selectedCountry,
        (name) => {
            if (globalStore.activePage !== "explore") return;

            if (name) {
                const city = dataStore.pinnedCities.find(
                    (c) => c.name === name,
                );
                if (city) cameraRig.focusOnLatLng(city.lat, city.lng);
            } else {
                cameraRig.clearFocus();
            }
        },
    );

    return { cameraRig };
}
